import { ModelDetails, Organisation } from '../common/organisation'
import { ONBOARDING_STEPS, USER_ROLE, User } from '../common/user'
import { DEFAULT_URL } from '../app-urls'

// what a new user is offered next. Each screen asks here rather than deciding for itself, so the steps of getting
// started stay in one order. Learning material (walkthrough, tutorials, videos) is offered, never forced
export enum ONBOARDING_ACTION {
    NONE = "none",
    CREATE_ORGANISATION = "createOrganisation",     // belongs to no organisation of their own: create one with its first model
    CREATE_FIRST_MODEL = "createFirstModel",        // their organisation has no models yet
    HOME_INTRO = "homeIntro",                       // first visit to a home page that has models: explain it
}

// the choices the getting-started dialogs offer, as the dialogs return them
export enum ONBOARDING_CHOICE {
    CREATE = "create",
    VIDEOS = "onboarding",
    WALKTHROUGH = "walkthrough",
    TUTORIAL = "tutorial",
    TOUR = "tour",
    HOME = "home",
}

export const hasDoneStep = (user:User|undefined, step:ONBOARDING_STEPS) => !!user?.onboardingSteps?.includes(step)

// the demo organisation is shared with everyone, so it doesn't count as theirs
export const onboardingForOrganisations = (organisations:Organisation[], demoOrganisationKey?:string) =>
    organisations.some(o => o._key !== demoOrganisationKey) ? ONBOARDING_ACTION.NONE : ONBOARDING_ACTION.CREATE_ORGANISATION

// where someone goes on signing in: the organisation they last used, unless that was the demo, which isn't theirs. Then
// it's the organisations list, which sends someone with no organisation of their own on to the ways to start
export const signedInUrl = (user:User, demoOrganisationKey?:string) =>
    user.currentOrganisationKey && user.currentOrganisationKey !== demoOrganisationKey ? DEFAULT_URL(user.currentOrganisationKey) : DEFAULT_URL()

// someone with no organisation of their own chooses how to start each time they come in: walk through the demo
// organisation, look round it, or create their own. Not without a demo organisation to show
export const offerStartChoices = (user:User|undefined, hasOwnOrganisation:boolean, demoOrganisationKey?:string) =>
    !!user && !!demoOrganisationKey && !hasOwnOrganisation

// at the end of the demo organisation's walkthrough, someone who hasn't made a model yet is invited to create their own
export const inviteToCreateOrganisation = (user:User|undefined, organisationKey:string|undefined, demoOrganisationKey?:string) =>
    !!user && user.role !== USER_ROLE.GUEST && !!organisationKey && organisationKey === demoOrganisationKey && !hasDoneStep(user, ONBOARDING_STEPS.FIRST_MODEL)

const canAddModels =(organisation:Organisation) => !!organisation.role && organisation.role <= USER_ROLE.WRITE

// only someone who can add a model is asked to create the first one. The home page is explained to someone without the
// checklist, which shows the way itself
export const onboardingForOrganisationHome = (user:User|undefined, organisation:Organisation|undefined) => {
    if (!user || !organisation) return ONBOARDING_ACTION.NONE
    if (!organisation.models?.length) {
        return canAddModels(organisation) ? ONBOARDING_ACTION.CREATE_FIRST_MODEL : ONBOARDING_ACTION.NONE
    }
    if (hasDoneStep(user, ONBOARDING_STEPS.HOME_INTRO_SHOWN) || gettingStartedChecklist(user, organisation).length > 0) return ONBOARDING_ACTION.NONE
    return ONBOARDING_ACTION.HOME_INTRO
}

// the getting started checklist on an organisation's home page: the things that show what LINQ can do for the user
export enum CHECKLIST_ITEM {
    FIRST_MODEL = "firstModel",
    FIRST_CHANGE = "firstChange",
    INSIGHTS = "insights",
    INVITE = "invite",
}

export interface ChecklistItem {
    item:CHECKLIST_ITEM
    done:boolean
}

const userCount = (organisation:Organisation) => organisation.users?.length ?? organisation.numUsers ?? 0

// the checklist's items in order, or none when it isn't shown: to someone who can't add models (they can't do the steps),
// once hidden, or once everything is done. Only admins can invite. users is the number in the organisation, when known better
// than the organisation itself says
export const gettingStartedChecklist = (user:User|undefined, organisation:Organisation|undefined, users?:number):ChecklistItem[] => {
    if (!user || !organisation || !canAddModels(organisation) || hasDoneStep(user, ONBOARDING_STEPS.CHECKLIST_HIDDEN)) return []

    const items:ChecklistItem[] = [
        {item:CHECKLIST_ITEM.FIRST_MODEL, done:!!organisation.models?.length},
        {item:CHECKLIST_ITEM.FIRST_CHANGE, done:hasDoneStep(user, ONBOARDING_STEPS.FIRST_MODEL_SAVE)},
        {item:CHECKLIST_ITEM.INSIGHTS, done:hasDoneStep(user, ONBOARDING_STEPS.FIRST_INSIGHTS)},
    ]
    if (organisation.role === USER_ROLE.ADMIN) items.push({item:CHECKLIST_ITEM.INVITE, done:(users ?? userCount(organisation)) > 1})

    return items.every(i => i.done) ? [] : items
}

// the model to make a first change in: the one most recently worked on
export const modelToOpen = (organisation:Organisation):ModelDetails|undefined => {
    const time = (m:ModelDetails) => new Date(m.updatedDate || m.createdDate || 0).getTime() || 0
    return [...(organisation.models || [])].sort((a, b) => time(b) - time(a))[0]
}
